import React, { useState, useEffect } from "react";
import { usePodeEditar } from "@hooks/usePodeEditar";
import { Link } from "react-router-dom";
import Select from "react-select";
import {
  planilhaService,
  type PlanilhaDuplicada,
} from "@services/planilhaService";
import { clienteService } from "@services/clienteService";
import { Planilha, PlanilhaStatus } from "@models/index";
import { rotuloStatusPlanilha } from "@utils/planilhaStatus";
import {
  Button,
  ServerDataTable,
  Column,
  DataTableAction,
  KebabMenu,
} from "@components/common";
import { EditarPlanilhaModal } from "@components/EditarPlanilhaModal";
import { PlanilhaStatusSelect } from "@components/PlanilhaStatusSelect/PlanilhaStatusSelect";
import { DuplicarPlanilhaModal } from "@components/DuplicarPlanilhaModal";
import { useServerPagination } from "@hooks/useServerPagination";
import { useFeedback } from "@contexts/FeedbackContext";
import { useNovidades } from "@hooks/useNovidades";
import NovidadesModal from "@components/NovidadesModal/NovidadesModal";
import {
  HiPlus,
  HiTrash,
  HiPencil,
  HiPencilAlt,
  HiDuplicate,
  HiLink,
  HiExternalLink,
  // HiChartBar,
} from "react-icons/hi";
import { FiLock } from "react-icons/fi";
import "./Planilhas.css";

export const Planilhas: React.FC = () => {
  const { confirm, showError, showSuccess } = useFeedback();
  // Visualizador abre e exporta planilhas, mas não cria, edita nem exclui.
  const podeEditar = usePodeEditar();
  const { aberto: novidadesAberto, fechar: fecharNovidades } = useNovidades();
  const [clientes, setClientes] = useState<Array<{ id: string; nome: string }>>(
    [],
  );
  const [clienteFilter, setClienteFilter] = useState<string>("");
  const [planilhaEmEdicao, setPlanilhaEmEdicao] = useState<Planilha | null>(
    null,
  );
  const [planilhaParaDuplicar, setPlanilhaParaDuplicar] =
    useState<Planilha | null>(null);

  // Hook de paginação server-side
  const {
    data: planilhas,
    loading,
    pagination,
    setPage,
    setPageSize,
    setSearch,
    setSortBy,
    setSortOrder,
    refresh,
    atualizarItem,
  } = useServerPagination({
    fetchFunction: planilhaService.getAll,
    initialPageSize: 10,
    initialSortBy: "createdAt",
    initialSortOrder: "desc",
  });

  // Carregar lista de clientes para o filtro
  useEffect(() => {
    const loadClientes = async () => {
      try {
        const response = await clienteService.getAll({
          page: 1,
          pageSize: 1000,
        });
        setClientes(response.items || []);
      } catch (err) {
        console.error("Erro ao carregar clientes:", err);
      }
    };
    loadClientes();
  }, []);

  const handleDelete = async (planilha: Planilha) => {
    const confirmed = await confirm({
      title: "Excluir planilha",
      message: `Tem certeza que deseja excluir a planilha "${planilha.evento}"? Esta ação não poderá ser desfeita.`,
      confirmText: "Excluir",
      cancelText: "Cancelar",
      tone: "danger",
    });

    if (!confirmed) return;

    try {
      await planilhaService.delete(planilha.id);
      refresh();
      showSuccess("Planilha excluída com sucesso.");
    } catch (err) {
      console.error(err);
      showError("Não foi possível excluir esta planilha.");
    }
  };

  const handleStatusChange = async (
    planilha: Planilha,
    novo: PlanilhaStatus,
  ) => {
    try {
      const { status } = await planilhaService.atualizarStatus(
        planilha.id,
        novo,
      );
      // Atualiza só a linha afetada: refazer a busca por causa de uma célula
      // devolveria o usuário ao topo da lista.
      atualizarItem((linha) => linha.id === planilha.id, { status });
      showSuccess(`Status alterado para "${rotuloStatusPlanilha(status)}".`);
    } catch (err) {
      console.error(err);
      showError("Não foi possível alterar o status desta planilha.");
    }
  };

  const handleDuplicada = (nova: PlanilhaDuplicada) => {
    showSuccess(
      `Planilha duplicada com ${nova.itensCopiados} item(ns) e ${nova.vinculosCopiados} fornecedor(es). Abrindo a cópia...`,
    );
    // Abre a cópia direto: quem duplica vai conferir a planilha em seguida, e
    // ela ainda precisa do Link PIT se ele não foi informado no modal.
    window.location.href = `/planilhas/editar/${nova.id}`;
  };

  const handleSortChange = (sortByKey: string, order: "asc" | "desc") => {
    setSortBy(sortByKey);
    setSortOrder(order);
  };

  const formatDate = (dateString: string | null | undefined) => {
    if (!dateString) return "-";
    try {
      const date = new Date(dateString + "T00:00:00");
      return date.toLocaleDateString("pt-BR");
    } catch {
      return "-";
    }
  };

  const formatDateRange = (
    dataInicio: string | null | undefined,
    dataFim: string | null | undefined,
  ) => {
    if (!dataInicio) return "-";
    const inicio = formatDate(dataInicio);
    if (dataFim && dataFim !== dataInicio) {
      const fim = formatDate(dataFim);
      return `${inicio} a ${fim}`;
    }
    return inicio;
  };

  const formatCurrency = (value?: number) => {
    if (!value) return "-";
    return new Intl.NumberFormat("pt-BR", {
      style: "currency",
      currency: "BRL",
    }).format(value);
  };

  // Definir colunas da tabela
  const columns: Column<Planilha>[] = [
    {
      key: "clienteNome",
      label: "Cliente",
      sortable: true,
      width: "15%",
    },
    {
      key: "evento",
      label: "Evento/Ação",
      sortable: true,
      width: "22%",
      render: (p) => <strong>{p.evento}</strong>,
    },
    {
      key: "dataInicio",
      label: "Data do Evento",
      sortable: true,
      width: "13%",
      render: (p) => formatDateRange(p.dataInicio, p.dataFim),
    },
    {
      key: "localEvento",
      label: "Local",
      sortable: false,
      width: "12%",
      render: (p) => p.localEvento || "-",
    },
    {
      key: "pit",
      label: "PIT",
      sortable: true,
      width: "8%",
      render: (p) => p.pit || "-",
    },
    {
      key: "numeroOC",
      label: "Nº OC",
      sortable: true,
      width: "8%",
      render: (p) => p.numeroOC || "-",
    },
    {
      key: "status",
      label: "Status",
      sortable: true,
      width: "10%",
      render: (p) => (
        <PlanilhaStatusSelect
          status={p.status}
          onChange={(novo) => handleStatusChange(p, novo)}
          disabled={!podeEditar}
        />
      ),
    },
    {
      key: "lockInfo" as keyof Planilha,
      label: "",
      sortable: false,
      width: "5%",
      render: (p) =>
        p.lockInfo ? (
          <span
            className="flex items-center justify-center gap-1 text-amber-500"
            title={`Em edição por ${p.lockInfo.usuarioNome}`}
          >
            <FiLock className="h-4 w-4" />
          </span>
        ) : null,
    },
    {
      key: "totalGeral",
      label: "Total",
      sortable: true,
      width: "12%",
      render: (p) => formatCurrency(p.totalGeral),
    },
  ];

  // Definir ações da tabela
  const actions: DataTableAction<Planilha>[] = [
    {
      icon: <HiPencil />,
      onClick: async (planilha) => {
        if (planilha.lockInfo) {
          const abrir = await confirm({
            title: "Planilha em uso",
            message: `Esta planilha está sendo editada por **${planilha.lockInfo.usuarioNome}**.\n\nDeseja abrir em modo somente leitura?`,
            confirmText: "Abrir (somente leitura)",
            cancelText: "Cancelar",
            tone: "danger",
          });
          if (!abrir) return;
        }
        window.location.href = `/planilhas/editar/${planilha.id}`;
      },
      variant: "primary",
      tooltip: (p) =>
        p.lockInfo
          ? `Em edição por ${p.lockInfo.usuarioNome} — clique para abrir em modo leitura`
          : "Editar planilha",
    },
    // {
    //   icon: <HiChartBar />,
    //   // Abre em nova aba: o painel é somente leitura e não adquire lock, então
    //   // consultar os indicadores não interfere em quem está editando.
    //   onClick: (planilha) =>
    //     window.open(`/planilhas/${planilha.id}/insights`, "_blank"),
    //   variant: "secondary",
    //   tooltip: "Ver insights da planilha",
    // },
    {
      icon: <HiExternalLink />,
      onClick: (planilha) => {
        if (planilha.linkWrike) {
          window.open(planilha.linkWrike, "_blank");
        } else {
          showError("Esta planilha não possui link do Wrike");
        }
      },
      variant: "success",
      tooltip: "Abrir no Wrike",
      disabled: (p) => !p.linkWrike,
    },
    {
      render: (planilha) => (
        <KebabMenu
          tooltip="Mais ações"
          items={[
            ...(podeEditar
              ? [
                  {
                    label: "Editar",
                    icon: <HiPencilAlt />,
                    onClick: () => setPlanilhaEmEdicao(planilha),
                  },
                  {
                    label: "Duplicar",
                    icon: <HiDuplicate />,
                    onClick: () => setPlanilhaParaDuplicar(planilha),
                  },
                ]
              : []),
            // Só aparece quando há link: um item desabilitado em toda planilha
            // sem PIT só ocupa espaço no menu.
            ...(planilha.linkProjeto
              ? [
                  {
                    label: "Ver PIT",
                    icon: <HiLink />,
                    onClick: () =>
                      window.open(
                        planilha.linkProjeto,
                        "_blank",
                        "noopener,noreferrer",
                      ),
                  },
                ]
              : []),
            ...(podeEditar
              ? [
                  {
                    label: "Excluir",
                    icon: <HiTrash />,
                    variant: "danger" as const,
                    onClick: () => handleDelete(planilha),
                  },
                ]
              : []),
          ]}
        />
      ),
    },
  ];

  // Filtrar planilhas por cliente selecionado
  const planilhasFiltradas = clienteFilter
    ? planilhas.filter((p) => p.clienteNome === clienteFilter)
    : planilhas;

  return (
    <div className="planilhas-page">
      <NovidadesModal aberto={novidadesAberto} onFechar={fecharNovidades} />

      <div className="page-header">
        <div>
          <h1>Gerenciamento de Planilhas</h1>
          <p>Crie e gerencie as planilhas do sistema</p>
        </div>
        <div className="flex items-center justify-end gap-3 mr-auto">
          {podeEditar && (
            <Link to="/planilhas/nova">
              <Button variant="primary">
                <HiPlus />
                Nova Planilha
              </Button>
            </Link>
          )}
        </div>
      </div>

      {/* Tabela com paginação server-side */}
      <ServerDataTable
        data={planilhasFiltradas}
        columns={columns}
        actions={actions}
        loading={loading}
        pagination={pagination}
        onPageChange={setPage}
        onPageSizeChange={setPageSize}
        onSearchChange={setSearch}
        searchPlaceholder="Buscar por cliente, evento, PIT, OC..."
        onSortChange={handleSortChange}
        keyExtractor={(planilha) => planilha.id}
        emptyMessage="Nenhuma planilha cadastrada ainda"
        filterComponent={
          <Select
            placeholder="Filtrar por cliente..."
            isClearable
            options={clientes.map((cliente) => ({
              value: cliente.nome,
              label: cliente.nome,
            }))}
            value={
              clienteFilter
                ? { value: clienteFilter, label: clienteFilter }
                : null
            }
            onChange={(option) => setClienteFilter(option?.value || "")}
            styles={{
              control: (base) => ({
                ...base,
                minHeight: "42px",
                borderColor: "#e5e7eb",
                "&:hover": {
                  borderColor: "#d1d5db",
                },
              }),
              menu: (base) => ({
                ...base,
                zIndex: 50,
              }),
            }}
            theme={(theme) => ({
              ...theme,
              colors: {
                ...theme.colors,
                primary: "#3b82f6",
                primary25: "#eff6ff",
              },
            })}
          />
        }
      />

      {/* Mensagem customizada quando não há dados */}
      {!loading && planilhas.length === 0 && pagination.totalItems === 0 && (
        <div className="empty-state-custom">
          <p>Nenhuma planilha cadastrada ainda</p>
          {podeEditar && (
            <Link to="/planilhas/nova">
              <Button variant="primary">
                <HiPlus />
                Criar Primeira Planilha
              </Button>
            </Link>
          )}
        </div>
      )}

      {/* Modal de edição rápida dos dados da planilha */}
      <EditarPlanilhaModal
        planilha={planilhaEmEdicao}
        isOpen={planilhaEmEdicao !== null}
        onClose={() => setPlanilhaEmEdicao(null)}
        onSaved={() => {
          refresh();
          showSuccess("Planilha atualizada com sucesso.");
        }}
        onError={(msg) => showError(msg)}
      />

      <DuplicarPlanilhaModal
        planilha={planilhaParaDuplicar}
        clientes={clientes}
        isOpen={planilhaParaDuplicar !== null}
        onClose={() => setPlanilhaParaDuplicar(null)}
        onDuplicated={handleDuplicada}
        onError={(msg) => showError(msg)}
      />
    </div>
  );
};
